import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";

/**
 * Confirmation avant de déclarer une commande payée par carte.
 *
 * N'apparaît que pour les clients **hors compte** : un client en compte n'est
 * pas concerné, sa facturation intervient en fin de mois et une livraison peut
 * encore être annulée. Ici le paiement est synchrone, et le déclarer engage un
 * encaissement.
 *
 * Les commandes #2521 et #2768 sont restées des mois sans facture : leur
 * StatuPaiement avait été passé à 3 depuis le back-office, et ce chemin
 * n'appelle pas le service de facturation — il n'y a donc eu ni ligne de
 * facture, ni numéro, ni PDF. Rien à l'écran ne le laissait deviner, le statut
 * se changeant dans une liste déroulante au milieu de vingt autres champs.
 *
 * Une modale « êtes-vous sûr ? » se clique sans lire au bout de trois fois.
 * D'où la case à cocher, placée APRÈS les conséquences : il faut les traverser
 * pour l'atteindre, et le geste devient une déclaration — l'opérateur n'accepte
 * pas une action, il affirme avoir constaté l'encaissement.
 *
 * C'est précisément ce qui manquait sur la #2521, passée de 2 à 3 sans jamais
 * passer par 4 : rien n'indique qu'un paiement ait eu lieu, et personne n'a eu
 * à l'affirmer.
 */
const ConfirmationPaiementCarte = ({ show, onClose, onConfirm, commandeId }) => {
  const [confirme, setConfirme] = useState(false);

  // Décochée à chaque ouverture : une case mémorisée d'une commande à l'autre
  // annulerait tout l'effet du dispositif.
  useEffect(() => {
    if (show) setConfirme(false);
  }, [show]);

  // A la creation, la commande n'a pas encore d'identifiant : afficher
  // « --commande= » suivi de rien donnerait une commande inutilisable, copiee
  // telle quelle. On rend alors la forme sans argument, qui traite tout le
  // retard d'un coup et reste juste.
  const commandeArtisan = commandeId
    ? `php artisan factures:regenerer-carte --commande=${commandeId} --appliquer`
    : `php artisan factures:regenerer-carte --appliquer`;

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.15rem" }}>
          Déclarer cette commande payée par carte ?
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <ul className="mb-3 ps-3">
          <li className="mb-2">
            La commande sera déclarée <strong>encaissée</strong> et sortira des commandes
            en attente de paiement.
          </li>
          <li className="mb-2">
            <strong>Aucune facture ne sera générée automatiquement.</strong> Le client
            verra pourtant un bouton « Télécharger la facture » sur sa commande, qui
            <strong> restera sans effet</strong> tant que le document n'existe pas.
          </li>
          <li className="mb-2">
            Pour produire le document ensuite :
            <code className="d-block mt-1 p-2 bg-light border rounded small">
              {commandeArtisan}
            </code>
          </li>
        </ul>

        <div className="border-top pt-3">
          <Form.Check
            type="checkbox"
            id="confirmation-encaissement"
            checked={confirme}
            onChange={(e) => setConfirme(e.target.checked)}
            label={
              <span>
                <strong>Je confirme avoir vérifié l'encaissement</strong> (Stripe ou
                relevé bancaire) <strong>et compris qu'aucune facture ne sera générée
                automatiquement.</strong>
              </span>
            }
          />
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>
          Annuler
        </Button>
        {/* Inactif tant que la case n'est pas cochée : c'est ce verrou qui donne
            sa valeur au dispositif. Actif d'emblée, la case ne serait qu'une
            décoration. */}
        <Button variant="success" disabled={!confirme} onClick={onConfirm}>
          Déclarer payée
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmationPaiementCarte;
